import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, forwardRef, input, signal} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';

type Fn = (a?: unknown) => void;

@Component({
  selector: 'lib-buttons-select',
  imports: [CommonModule],
  templateUrl: './buttons-select.component.html',
  styleUrls: ['./buttons-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ButtonsSelectComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonsSelectComponent<T extends {disabled?: boolean}, K extends keyof T> implements ControlValueAccessor {
  readonly options = input.required<T[]>();

  readonly keyName = input.required<K>();
  readonly optionLabel = input<K>('name' as K);
  readonly optionIcon = input<K>('icon' as K);
  readonly optionDataAttr = input<K>();

  readonly label = input<string>('');

  readonly iconSize = input<number | string>(18);
  protected readonly iconViewBox = computed(() => `0 0 ${this.iconSize()} ${this.iconSize()}`);

  protected isDisabled = signal(false);

  protected selectedOption = signal<T | undefined>(undefined);

  public cycle(): void {
    const selected = this.selectedOption();

    if (!selected) {
      this.onChangeValue(this.options()[0]);
      return;
    }

    const currentIndex = this.options().indexOf(selected);

    if (currentIndex < 0) {
      console.error('Option to be selected do not exist in the option set');
      this.onChangeValue(this.options()[0]);
      return;
    }

    const targetIndex = (currentIndex + 1) % this.options.length;

    this.onChangeValue(this.options()[targetIndex]);
  }

  onChangeValue(value: T | undefined): void {
    this.#changeValue(value);
    this.onChange(value);
  }

  onChange: Fn = () => {
  //   placeholder
  };
  onTouched: Fn = () => {
    //   placeholder
  };

  registerOnChange(fn: Fn) {
    this.onChange = fn;
  }

  registerOnTouched(fn: Fn) {
    this.onTouched = fn;
  }

  writeValue(value: T | string | number | undefined): void {
    if (typeof value === 'string' || typeof value === 'number') {
      const option = (this.options() || []).find(option => option[this.keyName()] === value);
      this.#changeValue(option);
      return;
    }
    this.#changeValue(value);
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  #changeValue(value: T | undefined): void {
    this.selectedOption.set(value);
    this.onTouched();
  }
}
